import { FC, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import {
  UseRefinementListProps,
  useInstantSearch,
  useRefinementList,
  useClearRefinements,
  useCurrentRefinements,
} from 'react-instantsearch';
import { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';

import { parseAlgoliaUrl } from '@utils/algolia/helpers';

import DropdownMenu from '_components/UI/DropdownMenu';
import SelectionCell from '_components/UI/SelectionCell';

import styles from './SizeBucketRefinement.module.scss';

export type SizeBucketRefinementProps = UseRefinementListProps & {
  locale?: string;
};

type RawResult = { facets?: Record<string, Record<string, number>> };

type InstantSearchResults = {
  results: {
    _rawResults?: RawResult[];
  };
};

type DropdownOption = {
  name: string;
  value: string;
  disabled: boolean;
};

/** Facet value names for `facetName` across every raw Algolia response. */
const rawFacetValues = (
  rawResults: RawResult[],
  facetName: string
): string[] => {
  const values = new Set<string>();
  for (const raw of rawResults) {
    for (const value of Object.keys(raw.facets?.[facetName] ?? {})) {
      values.add(value);
    }
  }
  return [...values];
};

/** Shoe-size bucket names (`size.<bucket>`) present in the raw facets map. */
const rawBucketNames = (rawResults: RawResult[]): string[] => {
  const buckets = new Set<string>();
  for (const raw of rawResults) {
    for (const facetName of Object.keys(raw.facets ?? {})) {
      const fragments = facetName.split('size.');
      if (fragments.length > 1) buckets.add(fragments[1]);
    }
  }
  return [...buckets];
};

// Bucket order we want to surface first when present; anything else falls to the
// end. Lifted verbatim from the legacy Sidebar/SizeBucketRefinement.
const namePriorityList = [
  'Mondopoint',
  "US Men's",
  'US Hommes',
  "US Women's",
  'US Femmes',
  'EU',
  'Infants',
  'Bébés',
  'Kids',
  'Enfants',
  'Youth',
  'Jeunes',
  'Waist',
  'Alphabetical',
];

// Buckets whose sizes are self-contained and never carry a shoe width.
const widthlessSizeBuckets = [
  'Kids',
  'Jeunes',
  'Youth',
  'Enfants',
  'Infants',
  'Bébés',
  'Mondopoint',
];

const parseHelper = (label: string) => {
  if (!label) return 0;
  const parts = label.split(';');
  const correction = parts.length > 1 ? 0.5 : 0;

  // Infant sizes sort to the very top of a bucket.
  if (
    label.toLowerCase().includes('infant') ||
    label.toLowerCase().includes('bébé')
  ) {
    return -1000;
  }

  return parseFloat(parts[0]) + correction;
};

const sortAscending = (arr: RefinementListItem[]) =>
  arr.sort((a, b) => parseHelper(a.value) - parseHelper(b.value));

const SizeBucketRefinement: FC<SizeBucketRefinementProps> = ({ locale }) => {
  const { t } = useTranslation(['searchResults']);
  const router = useRouter();
  const { isGenderedMale, isGenderedFemale } = parseAlgoliaUrl(
    router.asPath,
    locale ?? 'en'
  );
  const plpGender = isGenderedMale
    ? 'mens'
    : isGenderedFemale
    ? 'womens'
    : null;

  const { results } = useInstantSearch() as unknown as InstantSearchResults;
  const rawResults = results?._rawResults ?? [];

  const bucketOptions: DropdownOption[] = rawBucketNames(rawResults)
    .sort((a, b) => {
      const aIndex = namePriorityList.indexOf(a);
      const bIndex = namePriorityList.indexOf(b);
      return (
        (aIndex === -1 ? 10000 : aIndex) - (bIndex === -1 ? 10000 : bIndex)
      );
    })
    .filter((bucket) => {
      // Unisex shoes are sized in men's and women's, so a women's PLP surfaces
      // men's buckets and vice versa. Drop the mismatched gendered buckets.
      const lower = bucket.toLowerCase();
      if (
        plpGender === 'mens' &&
        (lower.includes('women') || lower.includes('femmes'))
      ) {
        return false;
      }
      // Note: leading space so "women" is not matched by " men".
      if (
        plpGender === 'womens' &&
        (lower.includes(' men') || lower.includes('hommes'))
      ) {
        return false;
      }
      return true;
    })
    .map((bucket) => ({ name: bucket, value: bucket, disabled: false }));

  const [bucketName, setBucketName] = useState(bucketOptions[0]?.name ?? '');

  const dropdownOptions: DropdownOption[] =
    bucketOptions.length > 0
      ? bucketOptions
      : [{ name: bucketName, value: bucketName, disabled: false }];

  if (!dropdownOptions.some((option) => option.value === bucketName)) {
    setBucketName(dropdownOptions[0].value);
  }

  const refinementName = `size.${bucketName}`;

  const widthRefinement = useRefinementList({
    attribute: 'shoeWidth',
    limit: 100,
    operator: 'or',
  });

  const availableWidths = rawFacetValues(rawResults, 'shoeWidth');

  const onlyRegular =
    availableWidths.length === 1 &&
    (availableWidths[0] === 'Regular' || availableWidths[0] === 'Régulier');
  const shouldShowWidthDropdown =
    availableWidths.length > 0 &&
    !onlyRegular &&
    !widthlessSizeBuckets.includes(bucketName);

  const defaultWidthText = locale === 'fr' ? 'Tout' : 'All';
  const widthOptions: DropdownOption[] = [defaultWidthText]
    .concat(availableWidths)
    .map((option) => ({ name: option, value: option, disabled: false }));

  const { items, refine } = useRefinementList({
    attribute: refinementName,
    limit: 100,
    operator: 'or',
    transformItems: sortAscending,
  });

  const { items: currentRefinements } = useCurrentRefinements();
  const selectedWidthFilters = currentRefinements
    .filter((refinement) => refinement.label === 'shoeWidth')
    .map((refinement) => refinement.refinements[0].label);
  // Assume only one width can be refined at once.
  const widthName =
    selectedWidthFilters.length > 0
      ? selectedWidthFilters[0]
      : defaultWidthText;

  const clearSizeRefinements = useClearRefinements({
    includedAttributes: [refinementName],
  });
  const clearWidthRefinements = useClearRefinements({
    includedAttributes: ['shoeWidth'],
  });

  return (
    <div className={styles.root}>
      <div className={styles.dropdown}>
        <DropdownMenu
          id="size-bucket-select"
          elevated
          defaultText={t('search.facet.size_dropdown_default')}
          selectedOption={bucketName}
          data={dropdownOptions}
          handleChange={(value: string) => {
            setBucketName(value);
            clearSizeRefinements.refine();
          }}
        />
      </div>
      {shouldShowWidthDropdown && (
        <div className={styles.group}>
          <p className={styles.groupTitle}>{t('search.facet.shoeWidth')}</p>
          <div className={styles.dropdown}>
            <DropdownMenu
              id="size-width-select"
              elevated
              defaultText={t('search.facet.width')}
              selectedOption={widthName}
              data={widthOptions}
              handleChange={(value: string) => {
                clearWidthRefinements.refine();
                if (value !== defaultWidthText && widthRefinement?.refine) {
                  widthRefinement.refine(value);
                }
              }}
            />
          </div>
        </div>
      )}
      <div className={styles.chips}>
        {items.map((item) => (
          <SelectionCell
            key={`${refinementName}-${item.value}`}
            selected={item.isRefined}
            disabled={item.count === 0}
            onClick={() => {
              refine(item.value);
            }}
          >
            {item.label}
          </SelectionCell>
        ))}
      </div>
    </div>
  );
};

export default SizeBucketRefinement;
